import { Search2Icon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  type PortalMyBidDto,
  BidV2Status,
  PortalAwardOutcomeDto,
  PortalEventContextType,
  useGetPortalMyBidsQuery,
} from "../../../autogen/bff-api";
import type { TFunction } from "i18next";
import { useApiError } from "../../../common/errors/useApiError";
import { momentLocaleFor } from "../../../common/formatting/i18nFormatters";
import { usePortalEventUrls } from "../../sourcing-events/portal/usePortalEventUrls";
import { LifecycleFilterChips, type LifecycleFilter } from "../../sourcing-events/portal/components/LifecycleFilterChips";
import { PortalLifecycleTag } from "../../sourcing-events/portal/components/PortalLifecycleTag";
import { getTimeLeftMeta } from "../../sourcing-events/portal/utils/displayFormatters";
import { BidV2StatusTag } from "../sourcing-event/BidV2StatusTag";
import { countByLifecycle, isCompetitionOpen, myBidDeadlineText, myBidTagProps, sortByUrgency } from "./myBids";

const SEARCH_DEBOUNCE_MS = 300;

const isOpenDraft = (bid: PortalMyBidDto): boolean => bid.status === BidV2Status.Draft && isCompetitionOpen(bid);

export const awardedToText = (bid: PortalMyBidDto, t: TFunction): string => {
  switch (bid.awardOutcome) {
    case PortalAwardOutcomeDto.Winner:
      return t("Your company");
    case PortalAwardOutcomeDto.Withdrawn:
      return t("The award has been withdrawn");
    case PortalAwardOutcomeDto.NotSelected:
    case PortalAwardOutcomeDto.Rejected:
      return bid.awardedTo?.length ? bid.awardedTo.join(", ") : t("Another supplier");
    default:
      return "—";
  }
};

const matchesQuery = (bid: PortalMyBidDto, query: string): boolean => {
  if (!query) return true;
  const haystack = [bid.event.title, bid.event.buyerName, bid.event.referenceId ?? ""].join(" ").toLowerCase();
  return haystack.includes(query.toLowerCase());
};

export const SupplierBidsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = momentLocaleFor(i18n.language);
  const portalUrls = usePortalEventUrls();
  const { show } = useApiError();

  const { data, isLoading, error } = useGetPortalMyBidsQuery({});
  const bids = useMemo(() => data?.bids ?? [], [data]);

  const [searchTerm, setSearchTerm] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const [filter, setFilter] = useState<LifecycleFilter>("ALL");

  const searched = useMemo(() => bids.filter((bid) => matchesQuery(bid, query)), [bids, query]);
  const lifecycleCounts = useMemo(() => countByLifecycle(bids), [bids]);
  const visible = useMemo(
    () =>
      sortByUrgency(filter === "ALL" ? searched : searched.filter((bid) => bid.event.lifecycle.status === filter)),
    [searched, filter]
  );

  const openBid = (bid: PortalMyBidDto) =>
    navigate(
      bid.event.contextType === PortalEventContextType.Private
        ? portalUrls.privateBid.edit.go(bid.eventId, bid.bidId)
        : portalUrls.bid.edit.go(bid.eventId, bid.bidId).intro,
    );
  const openRow = (bid: PortalMyBidDto) =>
    isOpenDraft(bid) ? openBid(bid) : navigate(portalUrls.view.go(bid.eventId));

  return (
    <>
      <Heading as="h1" size="lg">
        {t("My bids")}
      </Heading>
      <Text color="smMuted" mt="2">
        {t("Track the procurements you are bidding on")}
      </Text>

      <InputGroup size="lg" mt="6" mb="5">
        <InputLeftElement pointerEvents="none" h="full" display="flex" alignItems="center" justifyContent="center">
          <Search2Icon color="smTextDisabled" boxSize="4" />
        </InputLeftElement>
        <Input
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder={t("Search your bids") ?? ""}
          bg="smBackground"
          borderColor="smBorder"
          rounded="xl"
          focusBorderColor="smPrimary"
        />
      </InputGroup>

      {error ? (
        show(error)
      ) : isLoading ? (
        <Flex justify="center" py="20">
          <Spinner color="smPrimary" />
        </Flex>
      ) : bids.length === 0 ? (
        <EmptyCard text={t("You have not started any offers yet")} />
      ) : (
        <>
          <Box mb="5">
            <LifecycleFilterChips active={filter} counts={lifecycleCounts} onChange={setFilter} />
          </Box>

          {visible.length === 0 ? (
            <EmptyCard text={t("No offers match your current search")} />
          ) : (
            <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>{t("Procurement")}</Th>
                    <Th>{t("Competition")}</Th>
                    <Th>{t("My bid")}</Th>
                    <Th>{t("Awarded to")}</Th>
                    <Th>{t("Deadline")}</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {visible.map((bid) => (
                    <Tr key={bid.bidId} cursor="pointer" _hover={{ bg: "smPrimarySofter" }} onClick={() => openRow(bid)}>
                      <Td>
                        <Text fontWeight="medium" color="smText">
                          {bid.event.title || t("Untitled")}
                        </Text>
                        <Text fontSize="sm" color="smMuted">
                          {bid.event.buyerName}
                        </Text>
                      </Td>
                      <Td>
                        <PortalLifecycleTag status={bid.event.lifecycle.status} stage={bid.event.competitionStage} />
                      </Td>
                      <Td>
                        <BidV2StatusTag {...myBidTagProps(bid)} size="sm" />
                      </Td>
                      <Td>
                        <Text fontSize="sm" color={bid.awardOutcome ? "smText" : "smMuted"}>
                          {awardedToText(bid, t)}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="smMuted" whiteSpace="nowrap">
                          {myBidDeadlineText(bid, locale)}
                        </Text>
                        {isCompetitionOpen(bid) && bid.event.bidSubmissionDeadline && (
                          <TimeLeft deadline={bid.event.bidSubmissionDeadline} />
                        )}
                      </Td>
                      <Td textAlign="right" whiteSpace="nowrap">
                        <Button
                          size="sm"
                          variant="link"
                          colorScheme="teal"
                          onClick={(e) => {
                            e.stopPropagation();
                            openBid(bid);
                          }}
                        >
                          {isOpenDraft(bid) ? t("Continue") : t("View offer")}
                        </Button>
                        <Icon as={FiChevronRight} color="smMuted" ml="3" />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </>
      )}
    </>
  );
};

const TimeLeft = ({ deadline }: { deadline: string }) => {
  const { t } = useTranslation();
  const { label, color } = getTimeLeftMeta(deadline, t);
  return (
    <Text fontSize="xs" color={color}>
      {label}
    </Text>
  );
};

const EmptyCard = ({ text }: { text: string }) => (
  <Card variant="outline" rounded="xl">
    <CardBody py="20">
      <Flex align="center" justify="center">
        <Text color="smMuted">{text}</Text>
      </Flex>
    </CardBody>
  </Card>
);
